/*
 * useSWRCache — Stage-14 performance pass.
 *
 * Tiny stale-while-revalidate hook that fixes the "loads and loads" complaint
 * after returning from a detail page. Cached data is rendered IMMEDIATELY on
 * mount, then refetched in the background. The result is no perceived load
 * for navigation back from /documents/:id to /documents (or any cached route).
 *
 * Module-level Map cache keyed by URL path. TTL configurable per call. The
 * cache is intentionally process-local — survives route navigations within
 * a single SPA session, dies on full reload (and on logout).
 */
import { useEffect, useState, useRef } from 'react';
import axios, { API } from './api';

const _cache = new Map();   // key → { ts, data }
const _inflight = new Map(); // key → Promise

/**
 * Clear cached entries. Call on logout, or after a known mutation, with a
 * URL prefix to invalidate (e.g. clearSWRCache('/documents')).
 */
export const clearSWRCache = (prefix = '') => {
  if (!prefix) {
    _cache.clear();
    return;
  }
  for (const k of [..._cache.keys()]) {
    if (k.startsWith(prefix)) _cache.delete(k);
  }
};

/**
 * Synchronous peek into the cache. Returns the cached data (or null) without
 * triggering a fetch. Useful for components that want to hydrate state from
 * cache on initial mount and skip the loading skeleton on cache hit.
 *   const cached = peekSWRCache('/documents/abc123');
 */
export const peekSWRCache = (path) => {
  const entry = _cache.get(path);
  return entry ? entry.data : null;
};

/**
 * Manually warm the cache for a path — useful after writes.
 *   warmSWRCache('/documents', updatedDocs)
 */
export const warmSWRCache = (path, data) => {
  _cache.set(path, { ts: Date.now(), data });
};

/**
 * useSWRCache — drop-in replacement for `useEffect → axios.get → setState`.
 *
 *   const { data, loading, refetch } = useSWRCache('/documents', { ttlMs: 60_000 });
 *
 * Behaviour:
 *   - First call: cache miss → loading=true, fires fetch.
 *   - Cache hit: data instantly available, loading=false, also revalidates
 *     in background unless the entry is younger than ttlMs.
 *   - Concurrent calls for the same path dedupe to one inflight request.
 *   - `refetch()` forces a network round-trip and updates the cache.
 */
export const useSWRCache = (path, { ttlMs = 30_000, enabled = true } = {}) => {
  const cached = _cache.get(path);
  const [data, setData] = useState(cached ? cached.data : null);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  // Stage 20B+ — reset to true on (re)mount, set to false on unmount. React 18
  // StrictMode double-invokes effects in dev: without re-asserting `true` here,
  // a stale `false` from the first cleanup leaks into the second mount and
  // every setData() inside doFetch becomes a no-op. Production builds aren't
  // affected (StrictMode only doubles in dev) — the fix purely hardens dev/preview.
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const doFetch = async (force = false) => {
    if (!enabled) return;
    const now = Date.now();
    const entry = _cache.get(path);
    if (!force && entry && now - entry.ts < ttlMs) {
      // Fresh enough — skip network.
      return;
    }
    // Dedupe in-flight requests.
    if (!force && _inflight.has(path)) {
      try { await _inflight.get(path); } catch { /* swallow — handled below */ }
      const fresh = _cache.get(path);
      if (fresh && mountedRef.current) {
        setData(fresh.data);
        setLoading(false);
      }
      return;
    }
    const p = (async () => {
      try {
        const res = await axios.get(`${API}${path}`);
        _cache.set(path, { ts: Date.now(), data: res.data });
        if (mountedRef.current) {
          setData(res.data);
          setError(null);
        }
        return res.data;
      } catch (e) {
        if (mountedRef.current) setError(e);
        throw e;
      } finally {
        if (mountedRef.current) setLoading(false);
        _inflight.delete(path);
      }
    })();
    _inflight.set(path, p);
    try { await p; } catch { /* error already set in state */ }
  };

  useEffect(() => {
    // Always trigger — but if we already have cached data, the loading flag
    // is false and the user sees content instantly.
    doFetch(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, enabled]);

  return { data, loading, error, refetch: () => doFetch(true) };
};

export default useSWRCache;
